/**
 * Almacenamiento de imágenes, independiente del plan de ejecución:
 *   - Plan A (PWA en Fully Kiosk): CacheAssetStore  → Cache Storage + Service Worker
 *   - Plan B (APK Capacitor):      FilesystemAssetStore → @capacitor/filesystem
 *
 * Dos áreas:
 *   staging → descargas de una versión que todavía no está activa
 *   live    → archivos de la versión activa (lo único que ve la UI)
 *
 * Las claves son direccionadas por contenido (hash de la URL transformada, que incluye la versión
 * de Cloudinary), así que una misma clave siempre son los mismos bytes: los archivos que no
 * cambiaron entre versiones nunca se vuelven a descargar.
 */
export type Area = 'staging' | 'live';

export interface StorageInfo {
  usage?: number;
  quota?: number;
  persisted?: boolean;
}

export interface AssetStore {
  readonly kind: 'cache' | 'filesystem';
  init(): Promise<void>;
  keys(area: Area): Promise<Set<string>>;
  /** Guarda un archivo ya verificado */
  put(area: Area, key: string, blob: Blob): Promise<void>;
  /** Mueve/copia claves de staging a live (los que ya estén en live se ignoran) */
  promote(keys: string[]): Promise<void>;
  remove(area: Area, keys: string[]): Promise<void>;
  clear(area: Area): Promise<void>;
  /** URLs que la UI puede pintar de inmediato (sin esperas) para claves del área live */
  resolveUrls(keys: string[]): Promise<Map<string, string>>;
  /** Libera recursos de un resolveUrls anterior (p. ej. object URLs) */
  releaseUrls(urls: Map<string, string>): void;
  info(): Promise<StorageInfo>;
}

export async function storageInfo(): Promise<StorageInfo> {
  const out: StorageInfo = {};
  try {
    if (navigator.storage?.estimate) {
      const e = await navigator.storage.estimate();
      out.usage = e.usage;
      out.quota = e.quota;
    }
    if (navigator.storage?.persisted) out.persisted = await navigator.storage.persisted();
  } catch {
    /* sin soporte */
  }
  return out;
}
